import * as React from "react";
import TextField from "@material-ui/core/TextField";
import Autocomplete, {
  AutocompleteInputChangeReason,
} from "@material-ui/lab/Autocomplete";
import { RestService } from "../rest.service";
import {
  Entity,
  EntityQueryFilter,
  EntityQueryOrder,
  Ops,
} from "../entity.service";
import { first, throttle } from "lodash";
import { useEffect, useMemo, useState } from "react";

type LabelSelector = (entity: Entity) => string;

export interface EntitySelectSource {
  onSearch(props: EntitySelectProps, query: string): Promise<Entity[]>;
  onSingle(props: EntitySelectProps, id: string): Promise<Entity>;
}

export interface EntitySelectProps {
  restService: RestService;
  autoFocus?: boolean;

  source?: EntitySelectSource;

  disabled?: boolean;

  style?: React.CSSProperties;

  type: string;

  entityId?: any;
  entity?: any;

  title?: string;
  limit?: number;
  labelSelector: string | LabelSelector;

  searchFields?: string[];
  filters?: EntityQueryFilter[];
  orders?: EntityQueryOrder[];
  relations?: string[];
  className?: string;

  onSelect: (entity: Entity) => void | Promise<any>;
  onLoad?: (entity: Entity) => void | Promise<void>;
}

export const EntitySelect = (props: EntitySelectProps) => {
  const [value, setValue] = useState<Entity | null>(props.entity || null);
  const [inputValue, setInputValue] = useState("");

  const [options, setOptions] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(false);

  const [initialLoaded, setInitialLoaded] = useState(false);

  const source = useMemo(
    () => props.source || useEntitySource(),
    [props.source]
  );

  const fetch = useMemo(
    () =>
      throttle(
        (
          request: { input: string },
          callback: (results?: Entity[]) => void
        ) => {
          setLoading(true);

          source
            .onSearch(props, request.input)
            .then((entities) => (entities ? callback(entities) : () => {}))
            .finally(() => setLoading(false));
        },
        200
      ),
    [source]
  );

  const fetchSingle = async () => {
    const entity = await source.onSingle(props, props.entityId);

    if (entity) {
      props.onLoad && props.onLoad(entity);

      setValue(entity);
      setOptions((prev) => [
        ...prev.filter((candidate) => candidate.id !== entity.id),
        entity,
      ]);
    }
  };

  const optionLabel = (entity: Entity) => {
    if (typeof props.labelSelector === "string") {
      return entity[props.labelSelector];
    }

    return props.labelSelector(entity);
  };

  useEffect(() => {
    let active = true;

    fetch({ input: inputValue }, (results?: Entity[]) => {
      if (active) {
        setOptions(results);
      }
    });

    return () => {
      active = false;
    };
  }, [inputValue, fetch]);

  useEffect(() => {
    if (props.entityId && !initialLoaded) {
      fetchSingle();
    }

    setInitialLoaded(true);
  }, [props.entityId]);

  return (
    <Autocomplete
      disabled={props.disabled}
      className={props.className}
      style={Object.assign({ width: 300 }, props.style || {})}
      getOptionLabel={(option) => optionLabel(option)}
      getOptionSelected={(option, value) => option.id === value.id}
      options={options}
      filterOptions={(items) => items}
      autoComplete
      autoHighlight
      loading={loading}
      loadingText="Laden..."
      noOptionsText="Geen resultaten om weer te geven"
      includeInputInList={false}
      filterSelectedOptions={false}
      value={value}
      onChange={(event: any, newValue: Entity | null) => {
        setValue(newValue);

        props.onSelect(newValue);
      }}
      onInputChange={(
        event,
        newInputValue,
        reason: AutocompleteInputChangeReason
      ) => {
        if (reason !== "reset") {
          setInputValue(newInputValue);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus={props.autoFocus}
          label={props.title || "Zoeken"}
          variant="outlined"
          fullWidth
        />
      )}
    />
  );
};

class FilterBuilder {
  constructor(readonly inputText: string, readonly props: EntitySelectProps) {}

  *makeFilters() {
    for (const field of this.props.searchFields) {
      yield* Array.from(this.makeFilter(field));
    }
  }

  protected *makeFilter(field: string) {
    if (this.props.filters) {
      yield* this.props.filters;
    }

    yield {
      field: field,
      isOr: true,
      operator: "Like",
      value: this.inputText,
    } as EntityQueryFilter;
  }
}

export const useEntitySource = () =>
  ({
    onSearch: async (props, query) => {
      const response = await props.restService.post<Entity[]>(
        `entity/${props.type}/query`,
        {
          take: props.limit || 10,
          filters: Array.from(new FilterBuilder(query, props).makeFilters()),
          orders: props.orders || [],
          relations: props.relations || [],
        }
      );

      if (!response.hasError()) {
        return response.value;
      }
    },
    onSingle: async (props, id) => {
      const response = await props.restService.post<Entity[]>(
        `entity/${props.type}/query`,
        {
          take: 1,
          filters: [Ops.Field("id").Equals(id)],
          relations: props.relations || [],
        }
      );

      if (!response.hasError()) {
        return first(response.value);
      }
    },
  } as EntitySelectSource);
